import { Card, Divider, Grid, Typography, Box, Stack, Container } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Button from 'components/Common/Button';
import AssistanceFooter from 'components/Common/AssistanceFooter';
import DetailItem from 'components/Staff/ItemDetail';
import Profile from 'components/Staff/Profile';
import { useNavigate, useParams } from 'react-router-dom';
import Modal from 'components/Common/Modal';
import Routes from 'constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { fetchParishDetails } from 'redux/slices/parishesAndOrganisations/parishDetailsSlice';
import MuiLoader from 'components/Common/Loader';
import { formatParishAddress } from 'types/parishes';
import { selectStaffRoles } from 'redux/selectors/parishContext';
import { effectiveRoleForParish, hasAtLeastRole, MIN_PORTAL_STAFF_ROLE } from 'api/v1Helpers';

export default function OrganizationDetail() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const params = useParams();
    const [resetPasswordModal, setResetPasswordModal] = useState(false);
    const [suspendEmployeeModal, setSuspendEmployeeModal] = useState(false);
    const { details, loading } = useSelector((state: RootState) => state.parish.parishDetails);
    const staffRoles = useSelector(selectStaffRoles);
    // Route param remains `:companyId` but holds the parish UUID from /v1.
    const parishId = params.companyId;
    const { name, email, phone, companyCode, code } = details || {};

    // The directory lists every parish, but `POST …/payment-requests` is CLERK+ on that parish
    // (backend ParishRoleGuard). Offering the CTA regardless sent users to a form they could
    // never submit — gate it here so the dead end never opens.
    const canSendInvoiceHere = hasAtLeastRole(effectiveRoleForParish(staffRoles, parishId), MIN_PORTAL_STAFF_ROLE);

    useEffect(() => {
        if (parishId) {
            dispatch(fetchParishDetails({ parishId }));
        }
    }, [parishId, dispatch]);

    // Send Money CTA temporarily hidden — restore navigate(Routes.sendmoney.main, …) when remounting.
    const handleSendInvoice = () => {
        navigate(Routes.sendinvoice.main, {
            state: { parish: { name, companyCode: companyCode || code, companyId: parishId } },
        });
    };

    return (
        <Container maxWidth="xl">
            <Card
                sx={{
                    width: '100%',
                    p: '1rem',
                    my: 1,
                }}
            >
                <Stack m={'auto'} width={{ xs: '100%', md: '80%' }}>
                    <MuiLoader absolute loading={loading} />
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 2, marginTop: 10 }}>
                        <Typography sx={{ fontSize: 16, color: '#1F2937', fontWeight: 400 }} variant="h1">
                            Parish
                        </Typography>
                    </Box>
                    <Divider />
                    <Profile my={6} user={details} />
                    <Grid sx={{ my: 6 }} container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <DetailItem label="Entity name" value={name || ''} />
                            <DetailItem label="Email" value={email || ''} />
                            <DetailItem label="Phone" value={phone || ''} />
                            <DetailItem label="Code" value={companyCode || code || ''} />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <DetailItem label="Address" value={formatParishAddress(details)} />
                            <DetailItem label="Website" value={details?.website || details?.webSite || ''} />
                            <DetailItem label="Timezone" value={details?.timezone || ''} />
                            <DetailItem label="Status" value={details?.status || ''} />
                        </Grid>
                    </Grid>
                    {canSendInvoiceHere && (
                        <Stack direction={'row'} spacing={2}>
                            <Button width="100%" onClick={handleSendInvoice} title="Send Invoice" />
                        </Stack>
                    )}

                    <Modal isOpen={resetPasswordModal} onClose={() => setResetPasswordModal(false)}>
                        <>
                            <Typography sx={{ fontSize: 18, my: 2 }}>
                                A link to change password has been sent to the email address listed for this user. <br />
                            </Typography>
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                                <Button onClick={() => setResetPasswordModal(false)} width={152} title="Done" />
                            </div>
                        </>
                    </Modal>
                    <Modal isOpen={suspendEmployeeModal} onClose={() => setSuspendEmployeeModal(false)}>
                        <>
                            <Typography sx={{ fontSize: 18, my: 2 }}>
                                Once you disable an employee they will nolonger be able to log into the portal. You will be able to
                                reactivate the employee through the “Inactive” employee section. Are you sure you would like to disable this
                                employee? <br />
                            </Typography>
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around', marginTop: 32 }}>
                                <Button onClick={() => setSuspendEmployeeModal(false)} width={'45%'} title="Cancel" />
                                <Button onClick={() => setSuspendEmployeeModal(false)} width={'45%'} title="Confirm" />
                            </div>
                        </>
                    </Modal>

                    <AssistanceFooter />
                </Stack>
            </Card>
        </Container>
    );
}
